/**
 * Lado do widget das exportações em lote do histórico (rotas /api/call-exports):
 * criar, acompanhar e obter o link de download. O token de sessão vai nos headers
 * de autenticação (getAuthHeaders) como nas outras rotas.
 */
export type ExportKind = 'campaign' | 'manual';
export type ExportContent = 'audio' | 'data';

export interface CallExportSummary {
  id: string;
  kind: ExportKind | null;
  content: ExportContent | null;
  status: number;
  statusLabel: string;
  progress: number | null;
  fileSize: number | null;
  filename: string | null;
  createdAt: string | null;
  expiresAt: string | null;
}

export type CallExportDownload = { state: 'ready'; url: string; filename: string | null; fileSize: number | null; export: CallExportSummary } | { state: 'pending'; export: CallExportSummary } | { state: 'failed'; message: string; export?: CallExportSummary };

export const EXPORT_STATUS = { pending: 1, inProgress: 2, completed: 3 } as const;

export const EXPORT_KIND_LABELS: Record<ExportKind, string> = { manual: 'Chamadas manuais', campaign: 'Chamadas de campanha' };
export const EXPORT_CONTENT_LABELS: Record<ExportContent, string> = { audio: 'Áudios (zip)', data: 'Dados (planilha)' };

const dayInBrasilia = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);

/** últimos 30 dias, em AAAA-MM-DD de Brasília */
export function defaultExportRange(now: Date = new Date()): { from: string; to: string } {
  return { from: dayInBrasilia(new Date(now.getTime() - 30 * 24 * 3_600_000)), to: dayInBrasilia(now) };
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function describeExport(summary: CallExportSummary): string {
  const what = `${summary.kind ? EXPORT_KIND_LABELS[summary.kind] : 'Exportação'} · ${summary.content ? EXPORT_CONTENT_LABELS[summary.content].toLowerCase() : ''}`.replace(/ · $/, '');
  if (summary.status === EXPORT_STATUS.completed) return `${what}: pronta${summary.fileSize ? ` (${formatFileSize(summary.fileSize)})` : ''}`;
  if (summary.status === EXPORT_STATUS.inProgress) return `${what}: processando${summary.progress ? ` ${summary.progress}%` : ''}`;
  if (summary.status === EXPORT_STATUS.pending) return `${what}: na fila do Callix`;
  return `${what}: ${summary.statusLabel}`;
}

async function readError(response: Response, fallback: string): Promise<string> {
  const body = (await response.json().catch(() => null)) as { error?: unknown } | null;
  return typeof body?.error === 'string' && body.error ? body.error : fallback;
}

export async function createCallExport(apiBaseUrl: string, headers: Record<string, string>, request: { kind: ExportKind; content: ExportContent; from: string; to: string }): Promise<CallExportSummary> {
  const response = await fetch(`${apiBaseUrl}/api/call-exports`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, credentials: 'omit', body: JSON.stringify(request) });
  if (!response.ok) throw new Error(await readError(response, `Não foi possível pedir a exportação (${response.status})`));
  const body = (await response.json()) as { export: CallExportSummary };
  return body.export;
}

export async function listCallExports(apiBaseUrl: string, headers: Record<string, string>): Promise<CallExportSummary[]> {
  const response = await fetch(`${apiBaseUrl}/api/call-exports`, { method: 'GET', headers, credentials: 'omit' });
  if (!response.ok) throw new Error(await readError(response, `Não foi possível listar as exportações (${response.status})`));
  const body = (await response.json()) as { items?: CallExportSummary[] };
  return body.items ?? [];
}

/** estado atual e, quando pronta, o link assinado (válido por cerca de uma hora) */
export async function fetchCallExportDownload(apiBaseUrl: string, headers: Record<string, string>, id: string): Promise<CallExportDownload> {
  const response = await fetch(`${apiBaseUrl}/api/call-exports/${encodeURIComponent(id)}/download`, { method: 'GET', headers, credentials: 'omit' });
  const body = (await response.json().catch(() => null)) as { url?: string; filename?: string | null; fileSize?: number | null; export?: CallExportSummary; error?: string } | null;
  if (response.ok && body?.url && body.export) return { state: 'ready', url: body.url, filename: body.filename ?? null, fileSize: body.fileSize ?? null, export: body.export };
  if (response.status === 409 && body?.export) {
    return body.export.status <= EXPORT_STATUS.completed ? { state: 'pending', export: body.export } : { state: 'failed', message: body.error ?? `Exportação ${body.export.statusLabel}`, export: body.export };
  }
  return { state: 'failed', message: body?.error ?? `Não foi possível consultar a exportação (${response.status})`, export: body?.export };
}
